// The offline screens' reads (BL-2430, ADR 066 — Offline Mode § 3): the Auctions list, an
// auction's header and Lots tab, and Inventory, answered from what the sync engine stored
// (BL-2344). Each answer is the online route's own DTO; anything the device can't answer is
// a 404 or an empty list, never a guess.
import type { AuctionListItem } from '@auctogon/core-admin/schemas/responses/auction';
import type { OfflineDeltaLotDto } from '@auctogon/core-admin/schemas/responses/offline-sync';
import {
  countOfflineLots,
  offlineLotFilterOptions,
  offlineSalesRepOptions,
  queryOfflineLots,
} from '@auctogon/admin-isomorphic/isomorphic/offline/offline-lot-query';

import type { OfflineAuctionRecord, OfflineReferenceRecord, OfflineStore } from '../../gateways/native-device';
import { countOfflineAuctions, queryOfflineAuctions } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-auction-query';
import { OFFLINE_REFERENCE_KEY } from '../../stores/offline-sync/offline-sync-engine';
import type { OfflineRequest } from './create-offline-transport';
import { NOT_FOUND, SIGNED_OUT } from './offline-answers';
import { withCachedThumbnails } from './offline-thumbnails';

async function synced(store: OfflineStore): Promise<OfflineAuctionRecord[]> {
  return store.records.list({ collection: 'auctions' });
}

async function syncedAuction(store: OfflineStore, auctionId: string | undefined): Promise<OfflineAuctionRecord | null> {
  if (!auctionId) return null;
  return store.records.get({ collection: 'auctions', key: auctionId });
}

/**
 * The last decoded lot collection, keyed by its scope, by every synced auction's `syncedAt` and
 * by the local-write revision. The lists read it three times per screen load (list, counts,
 * filter options) and again on every keystroke; a sync re-stamps an auction's `syncedAt`, and an
 * offline write (BL-2431) bumps the revision, either of which changes the key.
 */
let lotsCache: { key: string; lots: OfflineDeltaLotDto[] } | null = null;

/**
 * Test seam: a test seeds a fresh store under the same scope and `syncedAt` as the last one.
 *
 * @returns nothing.
 * @sideEffect Drops the decoded lot collection.
 */
export function __resetOfflineLotsCacheForTest(): void {
  lotsCache = null;
}

/** Bumped by an offline write the decoded collection can't be patched for, so the lists re-read. */
let localWriteRevision = 0;

/**
 * Show an offline write (BL-2431) on the lists' next read: the decoded collection is patched with
 * the written lot when it belongs to the same scope, so the read never re-decodes every synced lot
 * for one change; otherwise the collection is dropped and re-read.
 *
 * @param input.scope - the store's tenant and user.
 * @param input.lot - the lot record the write put on the device.
 * @returns nothing.
 * @sideEffect Patches or invalidates the decoded lot collection.
 */
export function noteOfflineLotsChanged(input: { scope: { tenantId: string; userId: string }; lot: OfflineDeltaLotDto }): void {
  const { scope, lot } = input;
  if (!lotsCache || !lotsCache.key.startsWith(`${scope.tenantId}:${scope.userId}|`)) {
    localWriteRevision += 1;
    return;
  }
  const others = lotsCache.lots.filter((cached) => cached.detail.id !== lot.detail.id);
  lotsCache = { key: lotsCache.key, lots: [...others, lot] };
}

/** The lots of every synced auction (a lot's record is keyed by its id, so none repeats). */
async function syncedLots(store: OfflineStore): Promise<OfflineDeltaLotDto[]> {
  const versions = (await synced(store)).map((record) => `${record.auction.id}@${record.syncedAt}`).sort();
  const key = `${store.scope.tenantId}:${store.scope.userId}|${versions.join(',')}|${localWriteRevision}`;
  if (lotsCache?.key === key) return lotsCache.lots;
  const lots = await store.records.list({ collection: 'lots' });
  lotsCache = { key, lots };
  return lots;
}

async function reference(store: OfflineStore): Promise<OfflineReferenceRecord['reference'] | null> {
  const record = await store.records.get({ collection: 'reference', key: OFFLINE_REFERENCE_KEY });
  return record?.reference ?? null;
}

/** The synced auctions' list rows, which the offline query pages, filters and sorts as `GET /api/auctions` does. */
function listRows(records: OfflineAuctionRecord[]): AuctionListItem[] {
  return records.map((r) => r.listRow);
}

/**
 * Run a handler body with the active store, or answer as for a signed-out caller.
 *
 * @param request - The transport's request.
 * @param body - The handler body.
 * @returns The body's answer, or a 401.
 */
async function withStore<T>(request: OfflineRequest, body: (store: OfflineStore) => Promise<T>): Promise<T | typeof SIGNED_OUT> {
  const store = await request.openStore();
  return store ? body(store) : SIGNED_OUT;
}

/** The screen reads, keyed `"<registry key>:<VERB>"`; typed by `OfflineHandlers` where composed. */
export const screenReadHandlers = {
  // BL-2769: the page, facets, sort and counts the online route runs in SQL, over the synced rows.
  // The Deleted view (`status=deleted`) is empty: deleted auctions never sync.
  'auctions.list:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => ({
      kind: 'ok' as const,
      data: queryOfflineAuctions({ auctions: listRows(await synced(store)), query: req.query }),
    })),

  'auctions.counts:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => ({
      kind: 'ok' as const,
      data: countOfflineAuctions({ auctions: listRows(await synced(store)), query: req.query }),
    })),

  // The Auctions page's Sales rep facet: every active rep on the device.
  'auctions.filter_options:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => {
      return { kind: 'ok' as const, data: { salesReps: offlineSalesRepOptions((await reference(store))?.salesReps ?? []) } };
    }),

  'auctions.id.chrome:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => {
      const chrome = (await syncedAuction(store, req.params.id))?.auction.chrome ?? null;
      return chrome ? { kind: 'ok' as const, data: chrome } : NOT_FOUND;
    }),

  'auctions.id.detail:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => {
      const detail = (await syncedAuction(store, req.params.id))?.auction.detail ?? null;
      return detail ? { kind: 'ok' as const, data: detail } : NOT_FOUND;
    }),

  // The lot form's money fields only need the auction's currency (BL-2261: the ladder rides the snapshot).
  'auctions.id.bid_increments:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => {
      const record = await syncedAuction(store, req.params.id);
      return record ? { kind: 'ok' as const, data: { auctionId: record.auction.id, currency: record.auction.currency } } : NOT_FOUND;
    }),

  // No preview link offline: the bidder site is unreachable without a connection.
  'tenants.id.bidder_origin:GET': async () => ({ kind: 'ok' as const, data: { origin: '' } }),

  'inventory.index:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => {
      const page = queryOfflineLots({ lots: await syncedLots(store), query: req.query });
      return { kind: 'ok' as const, data: { ...page, items: await withCachedThumbnails({ store, rows: page.items }) } };
    }),

  'inventory.counts:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => ({ kind: 'ok' as const, data: countOfflineLots({ lots: await syncedLots(store), query: req.query }) })),

  'inventory.filter_options:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => {
      const [lots, auctions, ref] = await Promise.all([syncedLots(store), synced(store), reference(store)]);
      const data = offlineLotFilterOptions({
        lots,
        auctions: auctions.map((record) => ({ listRow: record.listRow, phaseKind: record.auction.phaseKind })),
        salesReps: ref?.salesReps ?? [],
        taxRates: ref?.taxRates ?? [],
      });
      return { kind: 'ok' as const, data };
    }),

  'tenants.id.lot_custom_fields:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => {
      const fields = (await reference(store))?.lotCustomFields ?? null;
      return fields ? { kind: 'ok' as const, data: fields } : NOT_FOUND;
    }),
};
